import { Game } from '@/types';
import { CalendarDays, DollarSign, Info } from "lucide-react";
import { differenceInDays, format } from "date-fns";
import { useState } from 'react';
import { Skeleton } from '@/components/ui/Skeleton';

interface Props {
  game: Game;
  onSelect: (game: Game) => void;
}

export function GameCard({ game, onSelect }: Props) {
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  const campaignLength = differenceInDays(
    new Date(game.endDate),
    new Date(game.startDate)
  );

  const firstVideo = game.gameplay && game.gameplay.length > 0 ? game.gameplay[0] : null;

  return (
    <button
      onClick={() => onSelect(game)}
      className="flex w-full gap-3 rounded-3xl border border-white/10 bg-zinc-900/70 p-4 text-left transition hover:border-white/20 h-full"
    >
      {/* Left column: content */}
      <div className="flex-1 flex flex-col gap-3">
        {/* Header: icon + name */}
        <div className="flex items-center gap-3">
          <img
            src={game.appIcon}
            alt={game.name}
            width={56}
            height={56}
            className="rounded-2xl border border-white/10"
          />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-white">{game.name}</h3>
          </div>
        </div>

        {/* Description under icon and name */}
        <div>
          <p className="text-sm text-white/60">{game.description}</p>
        </div>

        {/* Spacer to push stats to bottom */}
        <div className="flex-1" />

        {/* Stats boxes - flush to bottom */}
        <div className="grid grid-cols-3 gap-2 text-xs text-white/70 mt-auto">
          <div className="rounded-2xl bg-white/5 px-3 py-2">
            <p className="flex items-center gap-1 text-white">
              <DollarSign className="h-3 w-3" /> Budget
            </p>
            <p className="text-sm font-semibold text-green-300">
              ${game.totalBudget.toLocaleString()}
            </p>
          </div>
          <div className="rounded-2xl bg-white/5 px-3 py-2">
            <p className="flex items-center gap-1 text-white">
              <Info className="h-3 w-3" /> Max CPI
            </p>
            <p className="text-sm font-semibold text-sky-300">
              ${game.maxCpi.toFixed(2)}
            </p>
          </div>
          <div className="rounded-2xl bg-white/5 px-3 py-2">
            <p className="flex items-center gap-1 text-white">
              <CalendarDays className="h-3 w-3" /> Duration
            </p>
            <p className="text-sm font-semibold">{campaignLength} days</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-white/50 mt-2">
          <span>Campaign ends {format(new Date(game.endDate), "MMM d")}</span>
          <span className="font-medium text-white">View details →</span>
        </div>
      </div>

      {/* Right column: vertical video (15% smaller) */}
      <div className="flex-shrink-0" style={{ width: '119px' }}>
        {firstVideo ? (
          <div className="relative w-full h-full" style={{ aspectRatio: '9/16' }}>
            {!videoLoaded && (
              <Skeleton className="absolute inset-0 rounded-2xl" />
            )}
            <video
              src={firstVideo}
              className="w-full h-full rounded-2xl border border-white/10 object-cover"
              autoPlay
              muted
              loop
              playsInline
              onLoadedData={() => setVideoLoaded(true)}
              style={{ aspectRatio: '9/16' }}
            />
          </div>
        ) : (
          <div 
            className="w-full h-full rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center"
            style={{ aspectRatio: '9/16' }}
          >
            <span className="text-xs text-white/40 text-center px-2">Unavailable</span>
          </div>
        )}
      </div>
    </button>
  );
}
